import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { GridContainer } from 'components/shared/Grid/Grid.components';
import RecurrenceConfirmationDialog from '../RecurrenceConfirmationDialog/RecurrenceConfirmationDialog';
import {
  EditAppointmentTypeResponse,
  EventResponse,
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';

import type {
  AddedParticipant,
  ExistingParticipant
} from './EditParticipantsEntitySection';
import EditParticipantsEntitySection from './EditParticipantsEntitySection';
import {
  DrawerActions,
  DrawerContainer,
  DrawerContent,
  DrawerHeader
} from './EditParticipantsDialog.styles';
import { isPrimaryModifier } from 'components/shared/Typography/Kbd/Kbd.utils';
import { containsAppointmentInSeries } from '../../AppointmentsOverview.utils';

const ENTITY_TYPES: PlanningEntityType[] = ['EMPLOYEE', 'CLIENT', 'RESOURCE'];

type AddedByType = Record<PlanningEntityType, AddedParticipant[]>;

type EditParticipantsDrawerProps = {
  open: boolean;
  isLoading: boolean;
  selectedAppointments: EventResponse[];
  onConfirm: (
    addParticipants: PlanningEntityIdentifier[],
    removeParticipants: PlanningEntityIdentifier[],
    editAppointmentType: EditAppointmentTypeResponse
  ) => void;
  onClose: () => void;
};

const emptyAddedByType = (): AddedByType => ({
  EMPLOYEE: [],
  CLIENT: [],
  RESOURCE: [],
  WORKPLACE: [],
  PROJECT: [],
  ROUTE: [],
  ORGANIZATIONAL_UNIT: [],
  EMPLOYMENT: [],
  ORGANIZATIONAL_UNIT_TO_PLAN: []
});

const collectExistingParticipants = (
  appointments: EventResponse[]
): Record<PlanningEntityType, ExistingParticipant[]> => {
  const byType: Record<PlanningEntityType, Map<string, ExistingParticipant>> = {
    EMPLOYEE: new Map(),
    CLIENT: new Map(),
    RESOURCE: new Map(),
    WORKPLACE: new Map(),
    PROJECT: new Map(),
    ROUTE: new Map(),
    ORGANIZATIONAL_UNIT: new Map(),
    EMPLOYMENT: new Map(),
    ORGANIZATIONAL_UNIT_TO_PLAN: new Map()
  };

  for (const apt of appointments) {
    for (const emp of apt.employees ?? []) {
      const id = emp.entityId ?? String(emp.id);
      if (id && !byType.EMPLOYEE.has(id)) {
        byType.EMPLOYEE.set(id, {
          entityId: id,
          label:
            emp.nameInformal ??
            [emp.firstName, emp.lastName].filter(Boolean).join(' '),
          number: emp.number != null ? String(emp.number) : undefined
        });
      }
    }
    for (const client of apt.clients ?? []) {
      const id = client.id;
      if (id && !byType.CLIENT.has(id)) {
        byType.CLIENT.set(id, {
          entityId: id,
          label:
            client.displayName ??
            client.fullName ??
            [client.firstName, client.lastName].filter(Boolean).join(' '),
          number: client.number != null ? String(client.number) : undefined
        });
      }
    }
    for (const resource of apt.resources ?? []) {
      const id = resource.id;
      if (id && !byType.RESOURCE.has(id)) {
        byType.RESOURCE.set(id, {
          entityId: id,
          label: resource.name
        });
      }
    }
  }

  return {
    EMPLOYEE: Array.from(byType.EMPLOYEE.values()),
    CLIENT: Array.from(byType.CLIENT.values()),
    RESOURCE: Array.from(byType.RESOURCE.values()),
    WORKPLACE: [],
    PROJECT: [],
    ROUTE: [],
    ORGANIZATIONAL_UNIT: [],
    EMPLOYMENT: [],
    ORGANIZATIONAL_UNIT_TO_PLAN: []
  };
};

export default function EditParticipantsDrawer({
  open,
  isLoading,
  selectedAppointments,
  onConfirm,
  onClose
}: EditParticipantsDrawerProps) {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [cancelledAddedIds, setCancelledAddedIds] = useState<Set<string>>(
    new Set()
  );
  const [addedByType, setAddedByType] = useState<AddedByType>(emptyAddedByType);

  // Reset state whenever the drawer opens
  useEffect(() => {
    if (open) {
      setRemovedIds(new Set());
      setCancelledAddedIds(new Set());
      setAddedByType(emptyAddedByType());
    }
  }, [open]);

  const existingByType = useMemo(
    () => collectExistingParticipants(selectedAppointments),
    [selectedAppointments]
  );

  const handleToggleRemove = useCallback((entityId: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  }, []);

  const handleAddEntity = useCallback(
    (type: PlanningEntityType) =>
      (entity: PlanningEntityIdentifier, label: string) => {
        // Skip if already an existing participant on the selected appointments
        const alreadyExists = existingByType[type].some(
          (p) => p.entityId === entity.entityId
        );
        if (alreadyExists) return;

        setAddedByType((prev) => {
          const exists = prev[type].some(
            (p) => p.entity.entityId === entity.entityId
          );
          if (exists) return prev;
          return { ...prev, [type]: [...prev[type], { entity, label }] };
        });
      },
    [existingByType]
  );

  const handleToggleCancelAdded = useCallback((entityId: string) => {
    setCancelledAddedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  }, []);

  const collectChanges = useCallback(() => {
    const allAdd = ENTITY_TYPES.flatMap((type) =>
      addedByType[type]
        .filter((p) => !cancelledAddedIds.has(p.entity.entityId!))
        .map((p) => p.entity)
    );
    const allRemove: PlanningEntityIdentifier[] = [];
    for (const type of ENTITY_TYPES) {
      for (const p of existingByType[type]) {
        if (removedIds.has(p.entityId)) {
          allRemove.push({
            entityId: p.entityId,
            planningEntityType: type
          });
        }
      }
    }
    return { allAdd, allRemove };
  }, [addedByType, cancelledAddedIds, existingByType, removedIds]);

  const hasRecurringAppointments =
    containsAppointmentInSeries(selectedAppointments);

  const [recurrenceDialogOpen, setRecurrenceDialogOpen] = useState(false);

  const handleSaveClick = useCallback(() => {
    if (hasRecurringAppointments) {
      setRecurrenceDialogOpen(true);
    } else {
      const { allAdd, allRemove } = collectChanges();
      onConfirm(allAdd, allRemove, 'ONLY_THIS');
    }
  }, [hasRecurringAppointments, onConfirm, collectChanges]);

  const handleRecurrenceConfirm = (
    editAppointmentType: EditAppointmentTypeResponse
  ) => {
    const { allAdd, allRemove } = collectChanges();
    onConfirm(allAdd, allRemove, editAppointmentType);
    setRecurrenceDialogOpen(false);
  };

  const handleRecurrenceCancel = () => {
    setRecurrenceDialogOpen(false);
  };

  const handleClose = useCallback(() => {
    setRemovedIds(new Set());
    setCancelledAddedIds(new Set());
    setAddedByType(emptyAddedByType());
    onClose();
  }, [onClose]);

  const hasChanges =
    removedIds.size > 0 ||
    ENTITY_TYPES.some((type) =>
      addedByType[type].some((p) => !cancelledAddedIds.has(p.entity.entityId!))
    );

  // Close drawer on Escape (only when focus is within the drawer)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        containerRef.current?.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);

  // Save on Ctrl+S (only when focus is within the drawer)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === 's' &&
        isPrimaryModifier(event) &&
        containerRef.current?.contains(event.target as Node)
      ) {
        event.preventDefault();
        event.stopPropagation();
        if (hasChanges) handleSaveClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, hasChanges, handleSaveClick]);

  if (!open) return null;

  return (
    <DrawerContainer ref={containerRef}>
      <DrawerHeader>
        <Typography variant="h6">
          {t('appointmentsOverview.editParticipants.dialogTitle', {
            count: selectedAppointments.length
          })}
        </Typography>
      </DrawerHeader>

      <DrawerContent>
        <GridContainer>
          {ENTITY_TYPES.map((type) => (
            <EditParticipantsEntitySection
              key={type}
              planningEntityType={type}
              existingParticipants={existingByType[type]}
              removedEntityIds={removedIds}
              addedParticipants={addedByType[type]}
              cancelledAddedIds={cancelledAddedIds}
              onToggleRemove={handleToggleRemove}
              onAddEntity={handleAddEntity(type)}
              onToggleCancelAdded={handleToggleCancelAdded}
            />
          ))}
        </GridContainer>
      </DrawerContent>

      <DrawerActions>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="primary"
          disabled={!hasChanges}
          onClick={handleSaveClick}
        >
          {t('save')}
        </LoadingButton>
      </DrawerActions>

      <RecurrenceConfirmationDialog
        open={recurrenceDialogOpen}
        title={t(
          'appointmentsOverview.editParticipants.recurrenceDialog.title'
        )}
        description={t(
          'appointmentsOverview.editParticipants.recurrenceDialog.description'
        )}
        confirmLabel={t('appointmentsOverview.editParticipants.confirm')}
        onConfirm={handleRecurrenceConfirm}
        onCancel={handleRecurrenceCancel}
      />
    </DrawerContainer>
  );
}
